<template>
	<div class="w-full">
		<!-- Header -->
		<div class="mb-6">
			<h1
				class="text-2xl lg:text-3xl font-bold text-text-primary mb-2 tracking-tight"
			>
				Dashboard
			</h1>
			<p class="text-sm lg:text-base text-text-secondary">
				Your hub for managing policies, invoices, and exploring
				insurance options
			</p>
		</div>

		<!-- Stats Cards Grid (compact, equal-sized cards) -->
		<div class="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6 lg:mb-8">
			<StatsCard
				title="Quotations"
				:count="selectQuotationsData.length || 0"
				:to="{ name: 'NewPortalQuotations' }"
				borderClass="border-l-4 border-brand-cyan"
				iconClass="text-brand-cyan"
				ariaLabel="Open quotations"
			>
				<template #icon>
					<svg
						class="w-6 h-6 text-brand-cyan"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						stroke-width="2"
						stroke-linecap="round"
						stroke-linejoin="round"
						aria-hidden="true"
					>
						<path
							d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
						/>
						<polyline points="14 2 14 8 20 8" />
						<line x1="16" y1="13" x2="8" y2="13" />
						<line x1="16" y1="17" x2="8" y2="17" />
						<polyline points="10 9 9 9 8 9" />
					</svg>
				</template>
			</StatsCard>

			<StatsCard
				title="Active Policies"
				:count="selectPoliciesData.length || 0"
				:to="{ name: 'NewPortalUserProducts' }"
				borderClass="border-l-4 border-brand-navy"
				iconClass="text-brand-navy"
				ariaLabel="Open policies"
			>
				<template #icon>
					<svg
						class="w-6 h-6 text-brand-navy"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						stroke-width="2"
						stroke-linecap="round"
						stroke-linejoin="round"
						aria-hidden="true"
					>
						<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
					</svg>
				</template>
			</StatsCard>

			<StatsCard
				title="Paid Invoices"
				:count="getPaidInvoices()"
				:to="{ name: 'NewPortalInvoice', query: { status: 'PAID' } }"
				borderClass="border-l-4 border-green-900"
				iconClass="text-green-900"
				ariaLabel="Open paid invoices"
			>
				<template #icon>
					<svg
						class="w-6 h-6 fill-current text-green-900"
						viewBox="0 0 24 24"
						aria-hidden="true"
					>
						<path
							d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm-1 14.5L6.5 12l1.4-1.4L11 13.67l5.1-5.1L17.5 10l-6.5 6.5z"
						/>
					</svg>
				</template>
			</StatsCard>

			<StatsCard
				title="Unpaid Invoices"
				:count="getUnPaidInvoices()"
				:to="{ name: 'NewPortalInvoice', query: { status: 'UNPAID' } }"
				borderClass="border-l-4 border-brand-red"
				iconClass="text-brand-red"
				ariaLabel="Open unpaid invoices"
			>
				<template #icon>
					<svg
						class="w-6 h-6 text-brand-red"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						stroke-width="2"
						stroke-linecap="round"
						stroke-linejoin="round"
						aria-hidden="true"
					>
						<circle cx="12" cy="12" r="10" />
						<line x1="12" y1="8" x2="12" y2="12" />
						<line x1="12" y1="16" x2="12.01" y2="16" />
					</svg>
				</template>
			</StatsCard>
		</div>

		<div
			v-if="catalogues.length > 0"
			class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6 lg:mb-8"
		>
			<!-- Main column (spans 2 on large screens) -->
			<div class="lg:col-span-2 space-y-6">
				<!-- Insurance Catalogue Section -->
				<div
					class="bg-white rounded-lg border border-border shadow-sm overflow-hidden"
				>
					<!-- Header -->
					<div class="p-5 lg:p-6 border-b border-border">
						<div
							class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
						>
							<div class="flex items-center gap-3">
								<div
									class="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center"
								>
									<i
										class="fas fa-store text-primary text-lg"
									></i>
								</div>
								<div>
									<h2
										class="text-base lg:text-lg font-semibold text-text-primary"
									>
										Insurance Products
									</h2>
									<p
										class="text-xs text-text-secondary mt-0.5"
									>
										Explore tailored protection plans
									</p>
								</div>
							</div>
							<span
								class="inline-flex items-center text-xs text-text-secondary bg-surface px-3 py-1.5 rounded-full font-medium"
							>
								<i class="fas fa-box text-primary mr-1.5"></i>
								{{ catalogues.length }} Products
							</span>
						</div>
					</div>

					<!-- Tabs -->
					<div
						class="px-5 lg:px-6 py-4 border-b border-border bg-surface"
					>
						<div class="flex gap-2">
							<button
								@click="activeTab = 'individual'"
								:class="[
									'flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200',
									activeTab === 'individual'
										? 'bg-primary text-white shadow-sm'
										: 'bg-white text-text-secondary hover:bg-gray-50 border border-border',
								]"
								:aria-selected="activeTab === 'individual'"
								role="tab"
							>
								<i class="fas fa-user text-sm"></i>
								Individual Insurance
							</button>

							<button
								@click="activeTab = 'business'"
								:class="[
									'flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200',
									activeTab === 'business'
										? 'bg-brand-cyan text-white shadow-sm'
										: 'bg-white text-text-secondary hover:bg-gray-50 border border-border',
								]"
								:aria-selected="activeTab === 'business'"
								role="tab"
							>
								<i class="fas fa-briefcase text-sm"></i>
								Business Insurance
							</button>
						</div>
					</div>

					<!-- Tab Content -->
					<div class="p-5 lg:p-6">
						<div
							v-if="filteredProducts.length > 0"
							class="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-3 lg:gap-5 auto-rows-fr"
						>
							<div
								v-for="(product, index) in filteredProducts"
								:key="`${activeTab}-${index}`"
								class="group cursor-pointer h-full"
								@click="showRequirements(product)"
							>
								<div
									class="bg-white rounded-lg border border-border hover:border-primary hover:shadow-lg transition-all duration-300 overflow-hidden h-full flex flex-col"
								>
									<!-- Image -->
									<div
										class="relative h-24 sm:h-28 md:h-36 overflow-hidden"
									>
										<img
											:src="product.image"
											:alt="product.name"
											class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
											@error="onImageError"
										/>
										<div
											class="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
										></div>
										<span
											class="absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-semibold text-white shadow-lg backdrop-blur-sm hidden md:inline-flex product-badge"
											:style="{
												backgroundColor:
													getCategoryColor(
														product.categoryColor,
													),
											}"
											>{{ product.category }}</span
										>
									</div>

									<!-- Content -->
									<div
										class="p-2 sm:p-4 flex flex-col flex-grow"
									>
										<h3
											class="text-sm font-bold text-text-primary mb-2 line-clamp-1 group-hover:text-primary transition-colors"
										>
											{{ product.name }}
										</h3>
										<p
											v-if="isMdOrAbove"
											class="text-xs text-text-secondary flex-grow line-clamp-2 mb-3 hidden md:block product-desc"
										>
											{{ product.description }}
										</p>

										<!-- Footer -->
										<!--
                    <div v-if="isMdOrAbove" class="hidden md:flex items-center justify-between pt-3 border-t border-border product-footer">
                      <span class="text-xs font-semibold text-primary group-hover:text-primary-dark transition-colors">View Details</span>
                      <div class="w-6 h-6 rounded-full bg-primary/10 group-hover:bg-primary flex items-center justify-center transition-all duration-300">
                        <i class="fas fa-arrow-right text-primary group-hover:text-white text-xs group-hover:translate-x-0.5 transition-all"></i>
                      </div>
                    </div>
                    -->
									</div>
								</div>
							</div>
						</div>

						<div v-else class="text-center py-12">
							<div
								class="inline-flex items-center justify-center w-16 h-16 rounded-lg bg-surface mb-4"
							>
								<i
									class="fas fa-box-open text-text-secondary text-2xl"
								></i>
							</div>
							<h3
								class="text-base font-semibold text-text-primary mb-2"
							>
								No Products Available
							</h3>
							<p class="text-sm text-text-secondary">
								Check back later for new insurance products
							</p>
						</div>
					</div>
				</div>
			</div>

			<div class="lg:col-span-1">
				<!-- Sticky container: both Quick Actions and TimeWidget will stick together on lg+ -->
				<div class="lg:sticky lg:top-20 space-y-3">
					<div
						class="bg-white rounded-lg border border-border shadow-sm p-4 lg:p-5 mb-0"
					>
						<div class="flex items-center gap-3 mb-4">
							<div
								class="w-9 h-9 bg-primary/10 rounded-md flex items-center justify-center"
							>
								<i
									class="fas fa-bolt text-primary text-base"
								></i>
							</div>
							<div>
								<h3
									class="text-sm font-semibold text-text-primary"
								>
									Quick Actions
								</h3>
								<p class="text-xs text-text-secondary mt-0.5">
									Shortcuts to common tasks
								</p>
							</div>
						</div>

						<div class="flex flex-col gap-2">
							<button
								type="button"
								@click="goTo({ name: 'NewPortalAccounts' })"
								class="w-full flex items-center gap-3 p-3 rounded-lg bg-surface hover:bg-gray-50 border border-border transition-shadow duration-150 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-brand-cyan group"
								aria-label="Open My Wallet"
							>
								<div
									class="w-10 h-10 bg-brand-navy text-white rounded-md flex items-center justify-center"
								>
									<i class="fas fa-wallet"></i>
								</div>
								<div class="flex-1 text-left">
									<div
										class="text-sm font-medium text-text-primary"
									>
										Wallet
									</div>
									<div class="text-xs text-text-secondary">
										View balance
									</div>
								</div>
								<span
									class="w-6 h-6 rounded-full bg-white flex items-center justify-center shadow-[0_1px_2px_rgba(51,65,85,0.06)] transition duration-200 ease-in-out transform group-hover:translate-x-1 group-hover:shadow-[0_12px_30px_rgba(51,65,85,0.16)]"
								>
									<i
										class="fas fa-chevron-right text-text-secondary text-xs transform transition-transform duration-200 ease-in-out group-hover:rotate-[12deg]"
									></i>
								</span>
							</button>

							<button
								type="button"
								@click="goTo({ name: 'insurance' })"
								class="w-full flex items-center gap-3 p-3 rounded-lg bg-surface hover:bg-gray-50 border border-border transition-shadow duration-150 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-brand-cyan group"
								aria-label="Create New Quote"
							>
								<div
									class="w-10 h-10 bg-brand-cyan text-white rounded-md flex items-center justify-center"
								>
									<i class="fas fa-plus-circle"></i>
								</div>
								<div class="flex-1 text-left">
									<div
										class="text-sm font-medium text-text-primary"
									>
										New Quote
									</div>
									<div class="text-xs text-text-secondary">
										Get an instant quote
									</div>
								</div>
								<span
									class="w-6 h-6 rounded-full bg-white flex items-center justify-center shadow-[0_1px_2px_rgba(51,65,85,0.06)] transition duration-200 ease-in-out transform group-hover:translate-x-1 group-hover:shadow-[0_12px_30px_rgba(51,65,85,0.16)]"
								>
									<i
										class="fas fa-chevron-right text-brand-cyan text-xs transform transition-transform duration-200 ease-in-out group-hover:rotate-[12deg]"
									></i>
								</span>
							</button>

							<button
								type="button"
								@click="goTo({ name: 'NewPortalUserProfile' })"
								class="w-full flex items-center gap-3 p-3 rounded-lg bg-surface hover:bg-gray-50 border border-border transition-shadow duration-150 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-brand-cyan group"
								aria-label="Open My Profile"
							>
								<div
									class="w-10 h-10 bg-accent text-white rounded-md flex items-center justify-center"
								>
									<i class="fas fa-user-circle"></i>
								</div>
								<div class="flex-1 text-left">
									<div
										class="text-sm font-medium text-text-primary"
									>
										Profile
									</div>
									<div class="text-xs text-text-secondary">
										Manage account
									</div>
								</div>
								<span
									class="w-6 h-6 rounded-full bg-white flex items-center justify-center shadow-[0_1px_2px_rgba(51,65,85,0.06)] transition duration-200 ease-in-out transform group-hover:translate-x-1 group-hover:shadow-[0_12px_30px_rgba(51,65,85,0.16)]"
								>
									<i
										class="fas fa-chevron-right text-accent text-xs transform transition-transform duration-200 ease-in-out group-hover:rotate-[12deg]"
									></i>
								</span>
							</button>

							<button
								type="button"
								@click="goTo({ name: 'NewPortalUserKYC' })"
								class="w-full flex items-center gap-3 p-3 rounded-lg bg-surface hover:bg-gray-50 border border-border transition-shadow duration-150 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-green-600 group"
								aria-label="Open KYC Verification"
							>
								<div
									class="w-10 h-10 bg-green-600 text-white rounded-md flex items-center justify-center"
								>
									<i class="fas fa-id-card"></i>
								</div>
								<div class="flex-1 text-left">
									<div
										class="text-sm font-medium text-text-primary"
									>
										KYC Verify
									</div>
									<div class="text-xs text-text-secondary">
										Complete verification
									</div>
								</div>
								<span
									class="w-6 h-6 rounded-full bg-white flex items-center justify-center shadow-[0_1px_2px_rgba(51,65,85,0.06)] transition duration-200 ease-in-out transform group-hover:translate-x-1 group-hover:shadow-[0_12px_30px_rgba(51,65,85,0.16)]"
								>
									<i
										class="fas fa-chevron-right text-green-600 text-xs transform transition-transform duration-200 ease-in-out group-hover:rotate-[12deg]"
									></i>
								</span>
							</button>
						</div>
					</div>

					<div
						class="bg-white rounded-lg border border-border shadow-sm p-3 lg:p-4"
					>
						<TimeWidget label="Local Time" />
					</div>

					<div>
						<RecentActivity />
					</div>
				</div>
			</div>
		</div>

		<div
			v-else
			class="text-center py-12 bg-white border border-border rounded-lg"
		>
			<div
				class="inline-flex items-center justify-center w-16 h-16 rounded-lg bg-surface mb-4"
			>
				<i class="fas fa-store-slash text-text-secondary text-2xl"></i>
			</div>
			<h3 class="text-lg font-semibold text-text-primary mb-2">
				No Products Available
			</h3>
			<p class="text-sm text-text-secondary mb-6">
				Our insurance catalogue is currently being updated
			</p>
		</div>
	</div>
</template>

<script>
import dashboard from '../rsc/stores/dashboard.store';
import products from '../../../components/insurance/products';
import { mapGetters, mapActions } from 'vuex';
import StatsCard from '../templates/others/stats-card.vue';
import TimeWidget from '../templates/others/time-widget.vue';
import RecentActivity from '../templates/others/recent-activity.vue';

export default {
	name: 'Dashboard',

	components: { StatsCard, TimeWidget, RecentActivity },

	beforeCreate() {
		Event.$emit('layout:loading:start', 'Loading dashboard...');
	},

	beforeDestroy() {
		// remove media query listener when component is destroyed
		try {
			if (this._mq) {
				if (this._mq.removeEventListener)
					this._mq.removeEventListener('change', this._mqListener);
				else if (this._mq.removeListener)
					this._mq.removeListener(this._mqListener);
			}
		} catch (e) {
			// ignore
		}
		this.$store.unregisterModule('dashboard');
	},

	data() {
		return {
			activeTab: 'individual',
			catalogues: [],
			isMdOrAbove: true, // reactive flag for viewport >= md (768px)
		};
	},

	computed: {
		...mapGetters(['products', 'invoices']),
		...mapGetters('quotations', [
			'selectQuotationsData',
			'selectPoliciesData',
		]),
		...mapGetters('invoices', ['selectInvoicesData']),

		filteredProducts() {
			return this.catalogues.filter(
				(product) => product.type === this.activeTab,
			);
		},
	},

	methods: {
		...mapActions(['fetchData', 'showInvoice']),

		onImageError(event) {
			event.target.src = '/img/avatars/2.png';
		},

		getCategoryColor(color) {
			const colors = {
				blue: '#3b82f6',
				red: '#ef4444',
				orange: '#f97316',
				pink: '#ec4899',
				purple: '#8b5cf6',
				emerald: '#10b981',
				green: '#22c55e',
				sky: '#0ea5e9',
				teal: '#14b8a6',
				yellow: '#eab308',
			};
			return colors[color] || '#6b7280';
		},

		showRequirements(product) {
			Event.$emit('requirements:show', { subClass: product.classCode });
		},

		async checkQuotation() {
			try {
				let quotation = await $session.get('quotation-item');
				if (quotation) {
					this.showRequirements({
						classCode: quotation.sub_class_code || 'motor',
					});
					this.$store.commit('current', 1);
					this.$store.commit('selectedProduct', quotation.quotation);
					this.$store.commit('showPaymentModal', true);
					this.$store.commit('quotationID', quotation.quotationID);
					await this.$store.dispatch('fetchFreeLimits');
					$session.remove('quotation-item');
				}
			} catch (err) {
				console.error('Error checking quotation:', err);
			}
		},

		getPaidInvoices() {
			return this.selectInvoicesData.filter(
				(invoice) => invoice.invoiceStatus === 'PAID',
			).length;
		},

		getUnPaidInvoices() {
			return this.selectInvoicesData.filter(
				(invoice) => invoice.invoiceStatus !== 'PAID',
			).length;
		},

		initializeCatalogues() {
			try {
				// Initialize catalogues with products
				this.catalogues = [
					...products.individual.map((item) => ({
						...item,
						code: item.classCode,
						type: 'individual',
					})),
					...products.business.map((item) => ({
						...item,
						code: item.classCode,
						type: 'business',
					})),
				];
			} catch (err) {
				console.error('Error initializing catalogues:', err);
				this.catalogues = [];
			}
		},

		goTo(route) {
			this.$router.push(route);
		},
	},

	async mounted() {
		try {
			// Register dashboard store
			this.$store.registerModule('dashboard', dashboard);

			// Initialize catalogues
			this.initializeCatalogues();

			// Fetch data
			await this.fetchData();

			// Check quotation
			await this.checkQuotation();
		} catch (error) {
			console.error('Error mounting dashboard:', error);
		} finally {
			// Stop loading
			Event.$emit('layout:loading:stop');
		}

		// responsive: track md breakpoint (768px) so we can conditionally render product meta on medium+ screens only
		try {
			const mq = window.matchMedia('(min-width: 768px)');
			this.isMdOrAbove = mq.matches;
			this._mq = mq;
			this._mqListener = (e) => {
				this.isMdOrAbove = e.matches;
			};
			if (mq.addEventListener)
				mq.addEventListener('change', this._mqListener);
			else if (mq.addListener) mq.addListener(this._mqListener);
		} catch (e) {
			// fallback: assume medium+ size
			this.isMdOrAbove = true;
		}
	},
};
</script>

<style scoped>
@media (max-width: 767px) {
	.product-desc,
	.product-badge {
		display: none !important;
	}
}
</style>
